import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { X } from "lucide-react";

import { SearchableMultiSelect } from "@/components/ui/SearchableMultiSelect";
import type { SearchableSelectOption } from "@/components/ui/SearchableSelect";
import { buildSalesOrderApprovalTicketPath } from "@/lib/hubEntityDetailPaths";

import {
  fetchQualityApprovalQuickSearch,
  type QualityApprovalSearchRow,
} from "./rmaApi";

function useDebouncedValue<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = window.setTimeout(() => setDebounced(value), delayMs);
    return () => window.clearTimeout(timer);
  }, [delayMs, value]);
  return debounced;
}

function approvalLabel(row: Pick<QualityApprovalSearchRow, "id" | "name">): string {
  const name = row.name?.trim();
  return name != null && name !== "" ? name : `Approval ${row.id}`;
}

function approvalDescription(row: QualityApprovalSearchRow): string {
  return [row.salesOrderNumber?.trim(), row.referenceNumber?.trim(), row.type?.trim()]
    .filter((part): part is string => part != null && part !== "")
    .join(" · ");
}

function toSearchOption(row: QualityApprovalSearchRow): SearchableSelectOption {
  return {
    value: String(row.id),
    label: approvalLabel(row),
    description: approvalDescription(row) || null,
  };
}

export function QualityApprovalPicker({
  id,
  values,
  knownLabels,
  disabled,
  onChange,
}: {
  id: string;
  values: number[];
  knownLabels?: Record<number, string | null | undefined>;
  disabled?: boolean;
  onChange: (approvalTicketIds: number[]) => void;
}): JSX.Element {
  const [searchValue, setSearchValue] = useState("");
  const [knownRows, setKnownRows] = useState<Map<number, QualityApprovalSearchRow>>(new Map());
  const debouncedSearch = useDebouncedValue(searchValue.trim(), 250);
  const lookupQuery = values[0] != null ? String(values[0]) : "";
  const searchQuery = debouncedSearch.length >= 1 ? debouncedSearch : lookupQuery;

  const searchResultsQuery = useQuery({
    queryKey: ["quality", "approval-quick-search", searchQuery],
    queryFn: () => fetchQualityApprovalQuickSearch(searchQuery),
    enabled: searchQuery.length >= 1,
    staleTime: 20_000,
  });

  useEffect(() => {
    const rows = searchResultsQuery.data?.rows ?? [];
    if (rows.length === 0) {
      return;
    }
    setKnownRows((current) => {
      const next = new Map(current);
      for (const row of rows) {
        next.set(row.id, row);
      }
      return next;
    });
  }, [searchResultsQuery.data?.rows]);

  const selectedRows = values.map((ticketId) => ({
    id: ticketId,
    chipLabel:
      knownRows.get(ticketId) != null
        ? approvalLabel(knownRows.get(ticketId)!)
        : knownLabels?.[ticketId]?.trim() || `Approval ${ticketId}`,
  }));

  const options = useMemo(() => {
    const rows = searchResultsQuery.data?.rows ?? [];
    const byId = new Map<number, QualityApprovalSearchRow>();
    for (const row of rows) {
      byId.set(row.id, row);
    }
    for (const ticketId of values) {
      const selected = knownRows.get(ticketId);
      if (selected != null) {
        byId.set(selected.id, selected);
      }
    }
    const fromRows = [...byId.values()].map(toSearchOption);
    const present = new Set(fromRows.map((option) => option.value));
    for (const selected of selectedRows) {
      if (!present.has(String(selected.id))) {
        fromRows.push({
          value: String(selected.id),
          label: selected.chipLabel,
        });
      }
    }
    return fromRows;
  }, [searchResultsQuery.data?.rows, knownRows, values, selectedRows]);

  return (
    <SearchableMultiSelect
      id={id}
      variant="chips"
      alwaysShowChips
      selectedValues={new Set(values.map(String))}
      onSelectedValuesChange={(next) => {
        const ids = [...next]
          .map((value) => Number(value))
          .filter((parsed) => Number.isFinite(parsed) && parsed >= 1);
        onChange(ids);
        setSearchValue("");
      }}
      options={options}
      searchValue={searchValue}
      onSearchChange={setSearchValue}
      placeholder="Search approval name, GSO, or ticket id"
      searchPlaceholder="Search approval name, GSO, or ticket id"
      emptyMessage={
        searchValue.trim().length < 1
          ? "Type to search Inside Sales approvals."
          : "No approvals match."
      }
      isLoading={searchResultsQuery.isFetching}
      disabled={disabled}
      panelMinWidth={320}
      renderChip={({ option, disabled: chipDisabled, onRemove }) => {
        const approvalTicketId = Number(option.value);
        const selected = selectedRows.find((row) => row.id === approvalTicketId);
        return (
          <span className="inline-flex max-w-full items-center gap-1 rounded-md bg-slate-100 px-2 py-0.5 text-xs text-slate-800">
            <Link
              to={buildSalesOrderApprovalTicketPath(approvalTicketId)}
              className="text-xs text-sky-800 hover:underline"
            >
              {selected?.chipLabel ?? option.label}
            </Link>
            <button
              type="button"
              className="rounded p-0.5 text-slate-500 hover:bg-slate-200 hover:text-slate-800 disabled:pointer-events-none"
              aria-label={`Remove ${selected?.chipLabel ?? option.label}`}
              disabled={chipDisabled}
              onClick={(event) => {
                event.stopPropagation();
                onRemove();
              }}
            >
              <X className="h-3 w-3" aria-hidden />
            </button>
          </span>
        );
      }}
    />
  );
}
